import type { ConsumerDeliveryDate } from '../types/dtos';
import { useT } from '../hooks/useT';

type Props = {
  dates: ConsumerDeliveryDate[];
  now: Date;
  selectedId: string | null;
  onChange: (id: string) => void;
  locale: string;
};

export function DatePicker({ dates, now, selectedId, onChange, locale }: Props) {
  const { t } = useT();

  return (
    <fieldset>
      <legend className="mb-2 text-base font-medium text-slate-900" id="date-picker-legend">
        {t('offer.pick_date')}
      </legend>
      <div role="radiogroup" aria-labelledby="date-picker-legend" className="space-y-2">
        {dates.map((d) => {
          const bookable = new Date(d.bookableUntil) > now;
          const dateLabel = formatDate(d.dateValue, locale);
          const windowLabel = d.windowStart && d.windowEnd
            ? `${formatTime(d.windowStart, locale)}–${formatTime(d.windowEnd, locale)}`
            : d.displayLabel ?? '';

          return (
            <label
              key={d.id}
              className={[
                'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition',
                bookable
                  ? 'border-slate-300 bg-white hover:border-brand focus-within:ring-2 focus-within:ring-brand-ring'
                  : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400',
                selectedId === d.id ? 'border-brand ring-2 ring-brand-ring' : '',
              ].join(' ')}
            >
              <input
                type="radio"
                name="delivery-choice"
                value={d.id}
                disabled={!bookable}
                checked={selectedId === d.id}
                onChange={() => onChange(d.id)}
                className="h-4 w-4 text-brand focus:ring-brand-ring"
              />
              <div className="flex-1">
                <div className={bookable ? 'font-medium' : 'line-through'}>{dateLabel}</div>
                {windowLabel && <div className="text-sm text-slate-600">{windowLabel}</div>}
              </div>
              {!bookable && <span className="text-xs italic">{t('offer.row_expired')}</span>}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function formatDate(iso: string, locale: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

function formatTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}
